import { TFunction } from 'i18next'
import AppRestService from '../rest/AppRestService'
import ProxyRestService from '../rest/ProxyRestService'
import TlsService from './TlsService'

class ProxyService {
  static async getProxies(setProxies: React.Dispatch<any>) {
    const proxyRuntimes = await ProxyRestService.getRuntimes()
    const proxyConfigs = await ProxyRestService.getConfigs()

    var proxies: any[] = []
    proxyRuntimes.forEach((runtime: any) => {
      var proxy = {
        ...runtime,
        ...proxyConfigs.find((config: any) => config.id === runtime.id),
      }
      proxies.push(proxy)
    })
    setProxies(proxies)
  }

  static async createProxyConfig(proxyConfig: any, setProxies: React.Dispatch<any>, setKeysWithCerts: React.Dispatch<any>, t: TFunction) {
    try {
      const editedProxyConfig = await ProxyRestService.create(proxyConfig)
      ProxyService.getProxies(setProxies)
      await TlsService.getTlsKeysWithCerts(setKeysWithCerts)
      return t('proxy.createProxySuccess', { name: editedProxyConfig.name })
    } catch (error) {
      return Promise.reject(error)
    }
  }

  static async updateProxyConfig(proxyConfig: any, setProxies: React.Dispatch<any>, setKeysWithCerts: React.Dispatch<any>, t: TFunction) {
    try {
      const editedProxyConfig = await ProxyRestService.update(proxyConfig)
      ProxyService.getProxies(setProxies)
      await TlsService.getTlsKeysWithCerts(setKeysWithCerts)
      return t('proxy.editProxySuccess', { name: editedProxyConfig.name })
    } catch (error) {
      return Promise.reject(error)
    }
  }

  static async deleteProxyConfig(proxyConfig: any, setProxies: React.Dispatch<any>, t: TFunction) {
    try {
      await ProxyRestService.delete(proxyConfig.id)
      ProxyService.getProxies(setProxies)
      return t('proxy.deleteProxySuccess', { name: proxyConfig.name })
    } catch (error) {
      return Promise.reject(error)
    }
  }

  static getAssignedApps(id: number) {
    return ProxyRestService.getAssignedApps(id)
  }

  static async assignApps(permissions: any[], proxyConfigId: number, proxyConfigName: string, setProxies: React.Dispatch<any>, t: TFunction) {
    await ProxyRestService.updateAssignedApps(
      proxyConfigId,
      permissions.filter((permission) => permission.isAssigned).map((permission) => permission.id)
    )
    await ProxyService.getProxies(setProxies)
    return t('proxy.assignAppsSuccess', { name: proxyConfigName })
  }

  static async getAppsWithAssigned(proxy: any) {
    const allApps: any[] = await AppRestService.get()
    const assignableApps = allApps.filter((app) => app.supportedProxytypes.includes(proxy.type))
    const proxyConfigApps: any[] = await ProxyRestService.getAssignedApps(proxy.id)

    const proxyConfigAppIds: number[] = proxyConfigApps.map((permission: { id: any }) => permission.id)

    assignableApps.forEach((permission) => {
      if (proxyConfigAppIds.includes(permission.id)) {
        permission.isAssigned = true
      } else {
        permission.isAssigned = false
      }
    })
    return assignableApps
  }

  static async getProxyRuntimes(setProxyRuntimes?: React.Dispatch<any>) {
    if (setProxyRuntimes) {
      const proxyRuntimes = await ProxyRestService.getRuntimes()
      setProxyRuntimes(proxyRuntimes)
    } else {
      return ProxyRestService.getRuntimes()
    }
  }

  static async startProxy(id: number, setProxies: React.Dispatch<any>, setLoading: React.Dispatch<any>) {
    setLoading(true)
    await ProxyRestService.startProxy(id)
    await ProxyService.getProxies(setProxies)
    setLoading(false)
  }

  static async restartProxy(id: number, setProxies: React.Dispatch<any>, setLoading: React.Dispatch<any>) {
    setLoading(true)
    await ProxyRestService.restartProxy(id)
    await ProxyService.getProxies(setProxies)
    setLoading(false)
  }

  static async stopProxy(id: number, setProxies: React.Dispatch<any>, setLoading: React.Dispatch<any>) {
    setLoading(true)
    await ProxyRestService.stopProxy(id)
    await ProxyService.getProxies(setProxies)
    setLoading(false)
  }
}

export default ProxyService
